import { useMutation } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import { Button, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  WasteManifestVendorsTableQuery,
  WasteManifestVendorsTableQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useMemo, useState } from "react";
import { prettyPrintDateFull } from "util/dates";

import {
  CREATE_WASTE_MANIFEST_VENDOR,
  DELETE_WASTE_MANIFEST_VENDOR,
  WASTE_MANIFEST_VENDORS_TABLE_QUERY,
} from "./api";
import { WasteVendorDialog, WasteVendorFormType } from "./WasteVendorDialog";

type WasteVendorRow = WasteManifestVendorsTableQuery["wasteManifestVendorsTable"]["items"][number];

export const WasteVendors = () => {
  const theme = useTheme();
  const alerts = useAlerts();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [duplicateVendorName, setDuplicateVendorName] = useState<string | null>(null);
  const [vendorToDelete, setVendorToDelete] = useState<WasteVendorRow | null>(null);

  const [createVendor, { loading: creating }] = useMutation(CREATE_WASTE_MANIFEST_VENDOR, {
    refetchQueries: [WASTE_MANIFEST_VENDORS_TABLE_QUERY],
  });

  const [deleteVendor, { loading: deleting }] = useMutation(DELETE_WASTE_MANIFEST_VENDOR, {
    refetchQueries: [WASTE_MANIFEST_VENDORS_TABLE_QUERY],
  });

  const handleAddVendor = async (data: WasteVendorFormType) => {
    await createVendor({
      variables: { input: { name: data.name } },
      onCompleted: () => {
        setDuplicateVendorName(null);
        alerts.success(`Added vendor "${data.name}"`);
        setShowAddDialog(false);
      },
      onError: (error) => {
        if (/already exists/i.test(error.message)) {
          setDuplicateVendorName(data.name);
          return;
        }

        alerts.error(`Error adding vendor: ${error.message}`);
      },
    });
  };

  const handleDeleteVendor = async () => {
    if (!vendorToDelete) return;
    await deleteVendor({
      variables: { id: vendorToDelete.id },
      onCompleted: () => {
        alerts.success(`Deleted vendor "${vendorToDelete.name}"`);
        setVendorToDelete(null);
      },
      onError: (error) => {
        alerts.error(`Error deleting vendor: ${error.message}`);
      },
    });
  };

  const columns: OmnisearchGridColDef<WasteVendorRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Vendor Name",
        flex: 1.5,
      },
      {
        field: "createdAt",
        headerName: "Created At",
        flex: 1,
        filterKeyType: "time",
        renderCell({ value }) {
          return value ? prettyPrintDateFull(value) : "";
        },
      },
      {
        field: "orgCount",
        headerName: "Orgs Using",
        flex: 0.6,
        type: "number",
        sortable: false,
        filterable: false,
        renderCell({ row, value }) {
          const orgNames = row.orgNames ?? [];
          if (!orgNames.length) return value;

          return (
            <Tooltip
              title={orgNames.join("\n")}
              slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
            >
              <span>{value}</span>
            </Tooltip>
          );
        },
      },
      {
        field: "manifestCount",
        headerName: "Manifests",
        flex: 0.6,
        type: "number",
        sortable: false,
        filterable: false,
      },
      {
        field: "actions",
        type: "actions",
        sortable: false,
        align: "right",
        getActions: ({ row }) => {
          const disabled = row.manifestCount > 0 || deleting;
          const tooltip =
            row.manifestCount > 0
              ? `Cannot delete: vendor is referenced by ${row.manifestCount} manifest${
                  row.manifestCount === 1 ? "" : "s"
                }`
              : "Delete Vendor";
          const action = (
            <GridActionsCellItem
              key="delete"
              onClick={() => setVendorToDelete(row)}
              label="Delete Vendor"
              icon={<Delete />}
              disabled={disabled}
            />
          );

          if (disabled) {
            return [
              <Tooltip title={tooltip} key="delete">
                <span>{action}</span>
              </Tooltip>,
            ];
          }

          return [
            <Tooltip title={tooltip} key="delete">
              {action}
            </Tooltip>,
          ];
        },
      },
    ],
    [deleting]
  );

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<
        WasteManifestVendorsTableQuery,
        WasteManifestVendorsTableQueryVariables,
        WasteVendorRow
      >
        dataQuery={WASTE_MANIFEST_VENDORS_TABLE_QUERY}
        persistenceKey="staff-waste-manifest-vendors"
        initialSortModel={[{ field: "name", sort: "asc" }]}
        excludeFilterColumns={["orgCount", "manifestCount"]}
        getCount={(data) => data.wasteManifestVendorsTable.count}
        getItems={(data) => data.wasteManifestVendorsTable.items}
        getRowId={(row) => row.id}
        showManageColumns={false}
        commandButtons={[
          <Button
            key="add-vendor-button"
            variant="contained"
            onClick={() => {
              setDuplicateVendorName(null);
              setShowAddDialog(true);
            }}
          >
            Add Vendor
          </Button>,
        ]}
        columns={columns}
        noDataMessage="No waste vendors found."
      />

      {showAddDialog && (
        <WasteVendorDialog
          open={showAddDialog}
          onClose={() => {
            setDuplicateVendorName(null);
            setShowAddDialog(false);
          }}
          onSave={handleAddVendor}
          saving={creating}
          duplicateName={duplicateVendorName}
        />
      )}

      <ConfirmDialog
        open={!!vendorToDelete}
        onClose={() => setVendorToDelete(null)}
        header={`Delete vendor "${vendorToDelete?.name}"?`}
        confirmText="Yes, delete"
        cancelText="Cancel"
        onConfirm={handleDeleteVendor}
        info={
          <Typography variant="body2" mb={theme.spacing(2)}>
            This vendor has no associated manifests. It will be removed from the OneSchema Vendor
            Waste Manifest picklist the next time a staff import is initialized.
          </Typography>
        }
        loading={deleting}
        disableConfirm={!vendorToDelete}
      />
    </Stack>
  );
};
